import "swiper/css/grid";
import { FC, useState, useEffect, useMemo, useCallback } from "react";
import classNames from "classnames";
import {
  Input,
  Space,
  Button,
  notification,
  Modal,
  Dropdown,
  Menu,
  Empty,
} from "antd";
import { DownOutlined } from "@ant-design/icons";

import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Grid } from "swiper/modules";

import { startCase, isEmpty, lowerCase, orderBy, sortBy } from "lodash";

import LayoutDashboard from "@/layouts/layoutDashboard";

import Spinner from "@/components/Spinner";

import ModalRequestCircleChampion from "@/components/pages/connection/ModalRequestCircleChampion";
import SearchProfiles from "@/components/pages/search/search-profiles";
import CardUserProfile from "@/components/v2/CardUserProfile/CardUserProfileView";

import {
  useDeleteChampion,
  useGetConnections,
  useSearchPendingUserChampion,
  useGetTeaPosts,
} from "@/hooks/useConnections";
import { LoadingOutlined } from "@ant-design/icons";
import ErrorEmptyV2, { whichErrorEmptyProps } from "@/components/v2/ErrorEmpty";
import { useRouter } from "next/router";
import AddCocCard from "@/components/pages/connection/AddCocCard";
import ModalPendingCoc, {
  typeCancelInvitation,
} from "@/components/pages/connection/ModalPendingCoc";
import ProfileCardPosted from "@/components/pages/profile/ProfileCardPosted";
import { fetchUserProfile, userBecomeFan } from "@/hooks/useUsers";
import { useShowModalResult } from "@/components/modal-result";
import useWindowResize from "@/hooks/useWindowResize";
import ConnectionUserProfile from "@/components/pages/connection/ConnectionsUserProfile";
import useUserGuideStoreStore from "@/store/useUserGuideStore";
import SwiperMC from "@/components/SwiperMC";
import { Star } from "@/assets/icons";
import useAuthStore from "@/store/useAuthStore";
import { useSearchProfiles } from "@/hooks/useSearch";
import { shuffleArray, truncateText } from "@/utils/helper";
import useMilken from "@/hooks/useMilken";
import { getStarsInfo } from "@/utils/getStarsInfo";
import useEnvironmentStore from "@/store/useEnvironmentStore";
import { useAnalytics } from "@/hooks/useAnalytics";
import { IoAdd } from "react-icons/io5";

export enum EFilterType {
  ALL = "ALL_CONNECTION",
  // FOLLOWERS = "FOLLOWERS",
  MENTOR = "MENTOR",
  // SUPER_MENTOR = "SUPER_MENTOR",
  MENTEE = "MENTEE",
  ALLY = "ALLY",
  CIRCLE_OF_CHAMPION = "CIRCLE_OF_CHAMPION",
}

const Connections: FC = () => {
  const authUser = useAuthStore.useUser();
  const userId = useMemo(() => authUser?.profile?.user_id, [authUser]);
  const { isConferenceOrIsMilken } = useMilken();
  const router = useRouter();
  const query = router.query;
  const { env } = useEnvironmentStore();

  const [api, contextHolder] = notification.useNotification();

  const isMobile = useWindowResize();

  const [visibleData, setVisibleData] = useState<number>(5);
  const [searchValue, setSearchValue] = useState("");
  const [hideConnectedIDs, setHideConnectedIDs] = useState<string[]>([]);
  const [connectingLoadingIDs, setConnectingLoadingIDs] = useState<string[]>(
    []
  );

  const [resultDataRecommendedConnection, setResultDataRecommendedConnection] =
    useState<any[]>([]);

  const [nextTokenRecommendedConnection, setNextTokenRecommendedConnection] =
    useState<string | undefined>(undefined);

  const [showModalRecommendation, setShowModalRecommendation] =
    useState<boolean>(false);
  // TODO: Simplify this 2 states, replace searchKeyword state using state from query URL
  // active search keyword
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  // input search keyword
  const [inputSearchKeyword, setInputSearchKeyword] = useState<string>("");

  const additionalInfo = {
    [EFilterType.ALL]: "",
    // [EFilterType.FOLLOWERS]: "",
    [EFilterType.CIRCLE_OF_CHAMPION]: `${isConferenceOrIsMilken ? "Circle of Champions is an honor bestowed upon 6 of your connections. " : "Circle of Champions is an honor bestowed upon the 6 most important connections to you within the organization. "}You can bestow this honor upon your Mentees, Mentors, Sponsors, or Connections. This is a great way to help them earn extra points or rewards.`,
    // [EFilterType.SUPER_MENTOR]:
    //   "Sponsors are Super Mentors who make their mentees into protégés.",
    [EFilterType.MENTOR]:
      "Sponsors are Super Mentors who make their mentees into protégés.",
    [EFilterType.MENTEE]: "",
    [EFilterType.ALLY]: "Connections are your peers.",
  };

  useEffect(() => {
    const { searchKeyword } = query;
    if (searchKeyword && typeof searchKeyword === "string") {
      setSearchKeyword(searchKeyword);
      setInputSearchKeyword(searchKeyword);
    } else {
      setSearchKeyword("");
      setInputSearchKeyword("");
    }
  }, [query]);

  const [isModalRequestChampion, setIsModalRequestChampion] =
    useState<boolean>(false);

  const [filterType, setFilterType] = useState<EFilterType>(EFilterType.ALL);

  const [modalCancelCocInvitationConfirm, modalCancelCocInvitationConfirmSet] =
    useState<null | (typeCancelInvitation & { name?: string })>(null);

  const { data, isLoading, isError, refetch } = useGetConnections({
    showConnectionOnly: true,
    showChampionUserOnly: filterType === EFilterType.CIRCLE_OF_CHAMPION,
    keyword: searchKeyword,
  });

  const userStore = useAuthStore().user;

  const { data: dataProfiles = {}, isLoading: isLoadingProfile }: any =
    useSearchProfiles();

  const global = dataProfiles.global || [];

  const {
    data: dataRecommendConnection,
    isLoading: isLoadingRecommendConnection,
    isFetching: isFetchingRecommendConnection,
    refetch: refetchRecommendConnection,
  } = useGetConnections(
    {
      showNonConnectionOnly: true,
      keyword: searchKeyword,
      limit: 25,
      nextToken: nextTokenRecommendedConnection,
      isRecommendConnection: true,
    },
    !!nextTokenRecommendedConnection || undefined,
    userStore?.bio?.user?.organizationGroup
  );

  const displayedRecommendedConnections = resultDataRecommendedConnection
    ?.slice(0, visibleData)
    .filter((item) => !hideConnectedIDs?.includes(item?.id));

  const dataMyConnection = useMemo(() => {
    if (!data?.data) {
      return [];
    }

    switch (filterType) {
      case EFilterType.ALL:
        return [...data.data].filter(
          (item) => item.connectionType || item.isChampion
        );

      case EFilterType.MENTOR:
        return [...data.data].filter(
          (item) =>
            item.connectionType === "MENTOR" ||
            item.connectionType === "SUPER_MENTOR"
        );

      case EFilterType.CIRCLE_OF_CHAMPION:
        return [...data.data].filter((item) => item.isChampion);

      default:
        return [...data.data].filter(
          (item) => item.connectionType === filterType
        );
    }
  }, [data, filterType]);

  const dataSearchConnections = useMemo(() => {
    return {
      global: global,
      connections: dataMyConnection,
    };
  }, [searchKeyword, global, dataMyConnection]);

  const {
    data: dataSearchPendingUserChampion = [],
    refetch: refetchSearchPendingUserChampion,
    isRefetching: isLoadingPendingUserChampion,
  } = useSearchPendingUserChampion();

  const isHR = useMemo(() => {
    return authUser?.bio?.user?.isHR || authUser?.profile?.isHR;
  }, [authUser]);

  const {
    data: teaData,
    isLoading: isLoadingTea,
    error: teaError,
    refetch: teaRefetch,
    isRefetching: isFetchingTea,
    hasNextPage,
    fetchNextPage: teaFetchNextPage,
    isFetchingNextPage,
  } = useGetTeaPosts(userId, isHR);

  const dataTeaPost = teaData?.pages.flatMap((page) => page.data) || [];

  const countAddUserChampionLeft = isLoadingPendingUserChampion ? (
    <LoadingOutlined shape={undefined} />
  ) : dataSearchPendingUserChampion ? (
    Math.max(
      0,
      6 -
        (dataSearchPendingUserChampion?.length +
          (dataMyConnection?.length as number))
    )
  ) : null;

  const [modalConfig, modalConfigSet] = useState<{
    key: "info" | "list-pending" | null;
  }>({ key: null });

  const [isLoadingCancelCocInvitation, isLoadingCancelCocInvitationSet] =
    useState(false);
  const [isLoadingGetCancelUser, isLoadingGetCancelUserSet] = useState(false);

  const emptyStateConvertToV2 = useMemo((): whichErrorEmptyProps => {
    switch (filterType) {
      case EFilterType.ALL:
        return "connections-all";
      case EFilterType.CIRCLE_OF_CHAMPION:
        return "connections-coc";
      // case EFilterType.SUPER_MENTOR:
      //   return "connections-sponsors";
      case EFilterType.MENTOR:
        return "connections-mentors";
      case EFilterType.MENTEE:
        return "connections-mentees";
      // case EFilterType.FOLLOWERS:
      //   return "connections-waves";
      case EFilterType.ALLY:
        return "connections-allies";

      default:
        return "connections-all";
    }
  }, [filterType]);

  const { successModal } = useShowModalResult();
  const { track } = useAnalytics();

  async function quickConnect(id: string, callBack?: () => void) {
    console.log("🚀 ~ quickConnect ~ id:", id);
    try {
      setConnectingLoadingIDs([...connectingLoadingIDs, id]);
      await userBecomeFan(id, true);
      setConnectingLoadingIDs(connectingLoadingIDs.filter((i) => i !== id));
      callBack?.();
      // currently filtered with this state
      setHideConnectedIDs((prev) => [...prev, id]);
      handleGetRecommendedConnection(false);
      const newResultDataRecommendedConnection =
        resultDataRecommendedConnection?.filter(
          (item) => item?.user?.id !== id
        );
      setResultDataRecommendedConnection(newResultDataRecommendedConnection);
      handleGetRecommendedConnection(true);
    } catch (error: any) {
      api.error({
        message: "Connection request failed",
        description:
          error?.errors?.[0]?.message ||
          "There was a problem when connection request process, please try again.",
      });
    }
  }

  const submitCancelInvitation = async (payload: typeCancelInvitation) => {
    isLoadingCancelCocInvitationSet(true);
    try {
      await useDeleteChampion(payload.id, payload._version as string);

      successModal({
        show: true,
        title: "Invitation Cancelled",
        body: `The invitation to the Circle of Champions for ${modalCancelCocInvitationConfirm?.name} has been cancelled`,
      });

      if (dataSearchPendingUserChampion.length - 1 === 0) {
        modalConfigSet({ key: null });
      }

      refetchSearchPendingUserChampion();
      refetch();
      modalCancelCocInvitationConfirmSet(null);
    } catch (error: any) {
      api.error({
        message: "Cancel Circle Champion Invitation failed",
        description:
          error?.errors?.[0]?.message ||
          "Failed to Cancel Invitaion for Circle Champion, please try again.",
      });
    } finally {
      isLoadingCancelCocInvitationSet(false);
    }
  };

  const onChoosedCancelCoc = async (payload: typeCancelInvitation) => {
    isLoadingGetCancelUserSet(true);
    try {
      const dataUserCancel = await fetchUserProfile(payload.id);

      modalCancelCocInvitationConfirmSet({
        id: dataUserCancel?.currentChampion?.id,
        _version: dataUserCancel?.currentChampion?._version,
        name: `${dataUserCancel?.user?.firstName} ${dataUserCancel?.user?.lastName}`,
      });
    } catch (error: any) {
      api.error({
        message: "Failure fetching",
        description:
          error?.errors?.[0]?.message || "Failed to fetch user profile",
      });
    } finally {
      isLoadingGetCancelUserSet(false);
    }
  };

  const handleGetRecommendedConnection = useCallback(
    async (isLoadMore?: boolean) => {
      const nextCount =
        visibleData + 5 >= resultDataRecommendedConnection?.length;
      if (isLoadMore) {
        if (nextCount) {
          if (dataRecommendConnection?.nextToken) {
            setNextTokenRecommendedConnection(
              dataRecommendConnection?.nextToken
            );
          } else {
            setVisibleData((prev) => prev + 5);
          }
        } else {
          setVisibleData((prev) => prev + 5);
        }
      }
    },
    [visibleData, resultDataRecommendedConnection, dataRecommendConnection]
  );

  const menuItems = Object.values(EFilterType)
    ?.filter((x) => x !== EFilterType.ALLY)
    ?.map((item: string) => ({
      key: item,
      label:
        item === EFilterType.MENTOR
          ? "Mentors & Sponsors"
          : item === EFilterType.ALLY
            ? "Connections"
            : startCase(
                `${item.replaceAll("_", " ").toLowerCase()}s`
              ).replaceAll("Of", "of"),
    }));

  const menu = (
    <Menu
      onClick={({ key }) => setFilterType(key as EFilterType)}
      selectedKeys={[filterType]}
      items={menuItems}
    />
  );

  useEffect(() => {
    if (router.query?.open && router.query?.open === "coc") {
      setIsModalRequestChampion(true);
    }
  }, [router]);

  useEffect(() => {
    if (dataRecommendConnection) {
      if (dataRecommendConnection?.data?.length > 0) {
        setResultDataRecommendedConnection((prev) => [
          ...prev,
          ...shuffleArray(dataRecommendConnection?.data),
        ]);
        setVisibleData((prev) => prev + 5);
      }
      if (dataRecommendConnection?.nextToken) {
        setNextTokenRecommendedConnection(dataRecommendConnection?.nextToken);
      }
    }
  }, [dataRecommendConnection]);

  useEffect(() => {
    refetchRecommendConnection();
  }, []);

  useEffect(() => {
    if (hasNextPage && dataTeaPost.length === 1) {
      teaFetchNextPage();
    }
  }, [dataTeaPost, hasNextPage]);

  const { setUG, steps, initial, isLoadingUG } = useUserGuideStoreStore();

  useEffect(() => {
    let timeout: any;
    if (!isLoadingRecommendConnection) {
      if (!isLoadingUG) {
        if (!steps?.connections?.done) {
          timeout = setTimeout(
            () => setUG({ enabled: true, initial: 7 }),
            1000
          );
        } else {
          setUG({ enabled: false, initial });
        }
      } else {
        setUG({ enabled: false, initial });
      }
    } else {
      setUG({ enabled: false, initial });
    }

    return () => clearTimeout(timeout);
  }, [steps, isLoadingRecommendConnection, isLoadingUG]);

  useEffect(() => {
    if (inputSearchKeyword === "") {
      router.replace({
        query: "",
      });
    }
  }, [inputSearchKeyword]);

  return (
    <>
      {contextHolder}
      <LayoutDashboard title={`My Connections`}>
        {isMobile && (
          <>
            <div className="bg-card py-4 left-0 right-0 px-4 z-[100] flex justify-center -ml-4 -mr-4">
              <div className="flex justify-between md:justify-end gap-x-2 w-full md:w-auto">
                {getStarsInfo(env).map((item, idx: number) => (
                  <div
                    className="flex items-start md:items-center gap-1"
                    key={`label-${idx}`}
                  >
                    <Star
                      size={isMobile ? 18 : 24}
                      color={item?.color as never}
                    />
                    <p className="text-sm text-neutral-400">{item?.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <h1 className="font-bold text-white text-2xl mb-6 mt-8">
              My Connections
            </h1>
          </>
        )}

        {isEmpty(searchKeyword) && (
          <section>
            <div className="flex justify-between items-center mb-8 mt-[16px]">
              <div className="flex items-center gap-2">
                <h2 className="text-[20px] lg:text-2xl font-bold text-white">
                  Recommended Connections
                </h2>
                <div
                  onClick={() => {
                    setShowModalRecommendation(true);
                  }}
                  className="cursor-pointer underline text-yellow text-base ml-1"
                >
                  View Directory
                </div>
              </div>

              <div className="hidden lg:flex justify-between md:justify-end gap-x-2 w-full md:w-auto">
                {getStarsInfo(env).map((item, idx: number) => (
                  <div className="flex items-center gap-1" key={`label-${idx}`}>
                    <Star
                      size={isMobile ? 18 : 24}
                      color={item?.color as never}
                    />
                    <p className="text-xs md:text-sm text-neutral-400">
                      {item?.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {isLoadingRecommendConnection &&
            isFetchingRecommendConnection &&
            !nextTokenRecommendedConnection ? (
              <div className="pt-10 flex w-full justify-center">
                <Spinner size="lg" />
              </div>
            ) : (
              Array.isArray(resultDataRecommendedConnection) &&
              resultDataRecommendedConnection?.length > 0 && (
                <>
                  {isMobile ? (
                    <Swiper
                      slidesPerView={1.8}
                      spaceBetween={16}
                      style={{ paddingTop: 60 }}
                      onReachEnd={() => handleGetRecommendedConnection(true)}
                      id="recommend-connection-component"
                      onSlideChange={() =>
                        track(
                          "Slide/Swiping on recommend connections",
                          "Interaction",
                          `Page ${router.pathname}`
                        )
                      }
                    >
                      {Array.isArray(displayedRecommendedConnections) &&
                        removeDuplicatesUserById(
                          displayedRecommendedConnections
                        ).map((item, idx) => (
                          <SwiperSlide
                            key={`recommend-${idx}`}
                            style={{ height: "auto" }}
                            onClick={() =>
                              track(
                                "Recommend connections clicked",
                                "Click",
                                `Page ${router.pathname}`
                              )
                            }
                          >
                            <ConnectionUserProfile
                              item={item}
                              className="h-full"
                              quickConnect
                              onQuickConnect={quickConnect}
                            />
                          </SwiperSlide>
                        ))}
                    </Swiper>
                  ) : (
                    <>
                      <div id="recommend-connection-component">
                        {Array.isArray(displayedRecommendedConnections) && (
                          <SwiperMC
                            props={{
                              onSlideChange: () =>
                                track(
                                  "Slide/Swiping on recommend connections",
                                  "Interaction",
                                  `Page ${router.pathname}`
                                ),
                              onClick: () =>
                                track(
                                  "Recommend connections clicked",
                                  "Click",
                                  `Page ${router.pathname}`
                                ),
                              breakpoints: {
                                1023: {
                                  slidesPerView: 3,
                                  grid: {
                                    rows: 1,
                                    fill: "row",
                                  },
                                },
                                1439: {
                                  slidesPerView: 5,
                                  grid: {
                                    rows: 1,
                                    fill: "row",
                                  },
                                },
                              },
                              slidesPerGroup: 2,
                              wrapperClass: "pt-[48px!important]",
                              spaceBetween: 16,
                              grabCursor: true,
                              modules: [Grid],
                              onReachEnd: () =>
                                !isEmpty(dataRecommendConnection?.nextToken) &&
                                handleGetRecommendedConnection(true),
                            }}
                            classNameSlide="mb-[80px!important] h-[auto!important]"
                            data={removeDuplicatesUserById([
                              ...displayedRecommendedConnections,
                            ])}
                            render={(item, idx) => (
                              <div
                                id={`recommend-connection-component-${idx}`}
                                className="h-full"
                              >
                                <ConnectionUserProfile
                                  quickConnect
                                  onQuickConnect={quickConnect}
                                  item={item}
                                />
                              </div>
                            )}
                          />
                        )}
                      </div>
                    </>
                  )}
                </>
              )
            )}

            {!isLoadingRecommendConnection &&
              resultDataRecommendedConnection?.length < 1 && (
                <div className="pb-6">
                  <ErrorEmptyV2 which="connections-recommedation" />
                </div>
              )}
          </section>
        )}

        <section>
          {isMobile ? (
            <div>
              <div className="flex justify-between mt-[34px]">
                <h2 className="text-[20px] lg:text-2xl font-bold text-white">
                  My Connections
                </h2>
                <div>
                  <div className="flex gap-4 w-full justify-between items-center">
                    <nav>
                      <Dropdown overlay={menu} trigger={["click"]}>
                        <Button type="text">
                          {filterType === EFilterType.MENTOR
                            ? "Mentors & Sponsors"
                            : filterType === EFilterType.ALLY
                              ? "Connections"
                              : startCase(
                                  `${filterType.replaceAll("_", " ").toLowerCase()}s`
                                ).replaceAll("Of", "of")}{" "}
                          <DownOutlined />
                        </Button>
                      </Dropdown>
                    </nav>
                  </div>
                </div>
              </div>
              <div>
                <div className="py-5">
                  <Input.Search
                    loading={isLoading}
                    placeholder="Find Connection"
                    allowClear
                    enterButton
                    value={inputSearchKeyword}
                    onChange={(e) => {
                      setInputSearchKeyword(e.target.value);
                    }}
                    onSearch={(value) => {
                      if (!value) {
                        return;
                      }
                      refetchRecommendConnection();
                      refetch();
                      setFilterType(EFilterType.ALL);
                      const queryParams = { searchKeyword: value, q: value };
                      router.replace({
                        query: queryParams,
                      });
                    }}
                  />
                </div>
                <section className="py-5">
                  {additionalInfo[filterType] && (
                    <p
                      className="mb-8 max-w-2xl text-xs lg:text-sm text-[#D9D9D9]"
                      dangerouslySetInnerHTML={{
                        __html: additionalInfo[filterType],
                      }}
                    />
                  )}
                  {isLoading ? (
                    <div className="pt-10 flex w-full justify-center">
                      <Spinner size="lg" />
                    </div>
                  ) : isEmpty(searchKeyword) ? (
                    Array.isArray(dataMyConnection) &&
                    dataMyConnection?.length > 0 &&
                    !isError ? (
                      <div>
                        <Swiper
                          slidesPerView={1.8}
                          spaceBetween={16}
                          style={{ paddingTop: 60 }}
                          onReachEnd={() =>
                            handleGetRecommendedConnection(true)
                          }
                          id="recommend-connection-component"
                        >
                          {filterType === EFilterType.CIRCLE_OF_CHAMPION && (
                            <SwiperSlide
                              key={`recommend-addcoc`}
                              style={{ height: "auto" }}
                            >
                              <AddCocCard
                                className="h-full"
                                variant={
                                  dataMyConnection.length === 6
                                    ? "reach-max"
                                    : "pending"
                                }
                                dataPending={dataSearchPendingUserChampion}
                                onClickAdd={() =>
                                  setIsModalRequestChampion(true)
                                }
                                totalLeft={countAddUserChampionLeft}
                                modalConfigSet={(payloadCancel) =>
                                  modalConfigSet({ key: payloadCancel.key })
                                }
                              />
                            </SwiperSlide>
                          )}
                          {Array.isArray(dataMyConnection) &&
                            dataMyConnection.map((item, idx) => (
                              <SwiperSlide
                                key={`recommend-${idx}`}
                                style={{ height: "auto" }}
                              >
                                <ConnectionUserProfile
                                  item={item}
                                  className="h-full"
                                />
                              </SwiperSlide>
                            ))}
                        </Swiper>
                      </div>
                    ) : filterType === EFilterType.CIRCLE_OF_CHAMPION ? (
                      dataMyConnection?.length === 0 &&
                      dataSearchPendingUserChampion?.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-x-4 gap-y-20 mb-10 pt-14">
                          <AddCocCard
                            variant="pending"
                            dataPending={dataSearchPendingUserChampion}
                            onClickAdd={() => setIsModalRequestChampion(true)}
                            totalLeft={countAddUserChampionLeft}
                            modalConfigSet={(payloadCancel) =>
                              modalConfigSet({ key: payloadCancel.key })
                            }
                          />
                        </div>
                      ) : (
                        <ErrorEmptyV2 which="connections-coc" />
                      )
                    ) : (
                      <ErrorEmptyV2 which={emptyStateConvertToV2} />
                    )
                  ) : (
                    <>
                      <div className="bg-neutral-800 px-4 py-[10px] mt-2 mb-4">
                        <p className="text-sm text-[#D9D9D9]">
                          My Connections (
                          {dataSearchConnections?.connections?.length || 0})
                        </p>
                      </div>
                      <Swiper
                        slidesPerView={1.8}
                        spaceBetween={16}
                        style={{ paddingTop: 60 }}
                        onReachEnd={() => handleGetRecommendedConnection(true)}
                        id="search-connection"
                      >
                        {dataSearchConnections?.connections.map(
                          (item: any, idx: number) => (
                            <SwiperSlide
                              key={`recommend-${idx}`}
                              style={{ height: "auto" }}
                            >
                              <ConnectionUserProfile
                                item={item}
                                className="h-full"
                              />
                            </SwiperSlide>
                          )
                        )}
                      </Swiper>
                      <div className="bg-neutral-800 px-4 py-[10px] mt-6 mb-4">
                        <p className="text-sm text-[#D9D9D9]">
                          Global Search (
                          {dataSearchConnections?.global?.length || 0})
                        </p>
                      </div>
                      <Swiper
                        slidesPerView={1.8}
                        spaceBetween={16}
                        style={{ paddingTop: 60 }}
                        onReachEnd={() => handleGetRecommendedConnection(true)}
                        id="search-global"
                      >
                        {dataSearchConnections?.global.map(
                          (item: any, idx: number) => (
                            <SwiperSlide
                              key={`recommend-${idx}`}
                              style={{ height: "auto" }}
                            >
                              <ConnectionUserProfile
                                item={item}
                                className="h-full"
                              />
                            </SwiperSlide>
                          )
                        )}
                      </Swiper>
                    </>
                  )}
                </section>
              </div>
            </div>
          ) : (
            <></>
            // <div className="flex gap-4 w-full justify-between items-center">
            //   <nav>
            //     //Filter Type Connection
            //     <Space size={"small"}>
            //       {Object.values(EFilterType).map((item, idx: number) => (
            //         <Button
            //           type={filterType === item ? "primary" : "text"}
            //           onClick={() => setFilterType(item)}
            //           key={`nav-connection-${idx}`}
            //         >
            //           {item === EFilterType.SUPER_MENTOR
            //             ? "Sponsor"
            //             : item === EFilterType.FOLLOWERS
            //               ? "Wave"
            //               : item === EFilterType.ALLY
            //                 ? "Allie"
            //                 : startCase(
            //                     item?.replaceAll("_", " ").toLowerCase(),
            //                   ).replaceAll("Of", "of")}
            //           s
            //         </Button>
            //       ))}
            //     </Space>
            //   </nav>
            // </div>
          )}
        </section>

        <div
          className={classNames(
            isMobile
              ? "grid grid-cols-6 gap-4 mt-[16px]"
              : `flex relative ${dataMyConnection?.length < 0 && "!top-[-30px]"}`
          )}
        >
          <div className={`${isMobile ? "col-span-6" : "p-4 flex-1 mr-5"}`}>
            <h3 className="text-base !text-[20px] font-bold text-white">
              The Tea
            </h3>
            <div className={`${isMobile ? "p-0 mt-[16px]" : "p-[20px"}`}>
              {isMobile ? (
                <SwiperMC
                  onReachEnd={() => {
                    if (hasNextPage) {
                      teaFetchNextPage();
                    }
                  }}
                  props={{
                    slidesPerView: 1,
                    spaceBetween: 16,
                  }}
                  classNameSlide="!h-auto"
                  id="the-post-connection-component"
                  data={Array.isArray(dataTeaPost) ? dataTeaPost : []}
                  alwaysShowNavigation
                  hideNavigation={false}
                  showNavigationOnMobile
                  navigationClassName="px-4"
                  render={(item, idx) => (
                    <ProfileCardPosted
                      type="THE_TEA"
                      dataPost={item}
                      key={`card-posted-${idx}`}
                      isVisiting
                      onSuccess={() => teaRefetch()}
                      className="h-full flex flex-col"
                      cardStyle={{
                        flex: 1,
                      }}
                    />
                  )}
                />
              ) : (
                Array.isArray(dataTeaPost) &&
                dataTeaPost.length > 0 &&
                dataTeaPost.map((item, idx: number) => (
                  <div className="my-8" key={`card-posted-${idx}`}>
                    <ProfileCardPosted
                      type="THE_TEA"
                      isVisiting
                      dataPost={item}
                      key={`card-posted-${idx}`}
                      onSuccess={() => teaRefetch()}
                    />
                  </div>
                ))
              )}

              {!isLoadingTea && dataTeaPost?.length < 1 && (
                <div className="pt-8 md:pt-[121px] max-w-[263px] mx-auto lg:max-w-none lg:mx-0">
                  <ErrorEmptyV2 which="connections-the-tea" />
                </div>
              )}

              {!isMobile && (
                <>
                  {isFetchingNextPage && (
                    <div className=" flex w-full items-center justify-center mt-2">
                      <Spinner size="md" />
                    </div>
                  )}
                  {!isLoadingTea &&
                    !isFetchingTea &&
                    !isFetchingNextPage &&
                    hasNextPage && (
                      <button
                        className="mt-4 text-sm text-primary item-center flex gap-2 m-auto"
                        onClick={() => {
                          teaFetchNextPage();
                        }}
                        disabled={isLoadingTea}
                      >
                        {isLoadingTea ? <LoadingOutlined /> : "Load More Posts"}
                      </button>
                    )}
                </>
              )}
            </div>
          </div>

          {!isMobile && (
            <div style={{ maxWidth: 400 }}>
              <div className="bg-card p-4">
                <div className="flex  justify-between">
                  <h3 className="text-base !text-[20px] font-bold text-white">
                    My Connections
                  </h3>
                  <div>
                    <div className="flex gap-4 w-full justify-between items-center">
                      <nav>
                        <Dropdown overlay={menu} trigger={["click"]}>
                          <Button type="text">
                            {filterType === EFilterType.MENTOR
                              ? "Mentors & Sponsors"
                              : filterType === EFilterType.ALLY
                                ? "Allie"
                                : startCase(
                                    `${filterType
                                      .replaceAll("_", " ")
                                      .toLowerCase()}s`
                                  ).replaceAll("Of", "of")}{" "}
                            <DownOutlined />
                          </Button>
                        </Dropdown>
                      </nav>
                    </div>
                  </div>
                </div>
                <div className="py-5">
                  <Input.Search
                    loading={isLoading}
                    placeholder="Find Connection"
                    allowClear
                    enterButton
                    value={inputSearchKeyword}
                    onChange={(e) => {
                      setInputSearchKeyword(e.target.value);
                    }}
                    onSearch={(value) => {
                      if (!value) {
                        return;
                      }
                      refetchRecommendConnection();
                      refetch();
                      setFilterType(EFilterType.ALL);
                      const queryParams = { searchKeyword: value, q: value };
                      router.replace({
                        query: queryParams,
                      });
                    }}
                  />
                </div>
                <section className="py-5 px-8">
                  {additionalInfo[filterType] && (
                    <p
                      className="mb-8 max-w-2xl text-xs lg:text-sm text-[#D9D9D9]"
                      dangerouslySetInnerHTML={{
                        __html: additionalInfo[filterType],
                      }}
                    />
                  )}
                  {isLoading ? (
                    <div className="pt-10 flex w-full justify-center">
                      <Spinner size="lg" />
                    </div>
                  ) : isEmpty(searchKeyword) ? (
                    Array.isArray(dataMyConnection) &&
                    dataMyConnection?.length > 0 &&
                    !isError ? (
                      <div>
                        {filterType === EFilterType.CIRCLE_OF_CHAMPION && (
                          <AddCocCard
                            variant={
                              dataMyConnection.length === 6
                                ? "reach-max"
                                : "pending"
                            }
                            className="!bg-neutral-800 !mb-[35px]"
                            dataPending={dataSearchPendingUserChampion}
                            modalConfigSet={(payloadCancel) =>
                              modalConfigSet({ key: payloadCancel.key })
                            }
                            onClickAdd={() => setIsModalRequestChampion(true)}
                            totalLeft={countAddUserChampionLeft}
                          />
                        )}

                        <SwiperMC
                          props={{
                            breakpoints: {
                              1023: {
                                slidesPerView: 1,
                                grid: {
                                  rows: 5,
                                  fill: "row",
                                },
                              },
                              1439: {
                                slidesPerView: 1,
                                grid: {
                                  rows: 5,
                                  fill: "row",
                                },
                              },
                            },
                            slidesPerGroup: 1,
                            wrapperClass: "pt-[48px!important]",
                            spaceBetween: 16,
                            grabCursor: true,
                            modules: [Grid],
                          }}
                          classNameSlide="mb-[80px!important] h-[auto!important]"
                          data={dataMyConnection} // data store
                          render={(item, idx) => (
                            <div
                              id={`recommend-${idx}`}
                              className="h-full bg-neutral-800"
                            >
                              <ConnectionUserProfile
                                item={item}
                                cardClassName="!bg-neutral-800"
                              />
                            </div>
                          )}
                        />
                      </div>
                    ) : filterType === EFilterType.CIRCLE_OF_CHAMPION ? (
                      dataMyConnection?.length === 0 &&
                      dataSearchPendingUserChampion?.length > 0 ? (
                        isMobile ? (
                          <div className="grid grid-cols-2 gap-x-4 gap-y-20 mb-10 pt-14 bg-neutral-800">
                            <AddCocCard
                              className="!bg-neutral-800"
                              variant="pending"
                              dataPending={dataSearchPendingUserChampion}
                              onClickAdd={() => setIsModalRequestChampion(true)}
                              totalLeft={countAddUserChampionLeft}
                              modalConfigSet={(payloadCancel) =>
                                modalConfigSet({ key: payloadCancel.key })
                              }
                            />
                          </div>
                        ) : (
                          <div className="gap-x-4 gap-y-20 mb-10 pt-14 bg-neutral-800">
                            <AddCocCard
                              className="!bg-neutral-800"
                              variant="pending"
                              dataPending={dataSearchPendingUserChampion}
                              onClickAdd={() => setIsModalRequestChampion(true)}
                              totalLeft={countAddUserChampionLeft}
                              modalConfigSet={(payloadCancel) =>
                                modalConfigSet({ key: payloadCancel.key })
                              }
                            />
                          </div>
                        )
                      ) : (
                        <ErrorEmptyV2 which="connections-coc" />
                      )
                    ) : (
                      <ErrorEmptyV2 which={emptyStateConvertToV2} />
                    )
                  ) : (
                    <>
                      <div className="bg-neutral-800 px-4 py-[10px] mt-6 mb-4">
                        <p className="text-sm text-[#D9D9D9]">
                          My Connections (
                          {dataSearchConnections?.connections?.length || 0})
                        </p>
                      </div>
                      <SwiperMC
                        props={{
                          breakpoints: {
                            1023: {
                              slidesPerView: 1,
                              grid: {
                                rows: 1,
                                fill: "row",
                              },
                            },
                            1439: {
                              slidesPerView: 1,
                              grid: {
                                rows: 1,
                                fill: "row",
                              },
                            },
                          },
                          slidesPerGroup: 1,
                          wrapperClass: "pt-[48px!important]",
                          spaceBetween: 16,
                          grabCursor: true,
                          modules: [Grid],
                        }}
                        classNameSlide="mb-[80px!important] h-[auto!important]"
                        data={dataSearchConnections?.connections || []}
                        render={(item, idx) => (
                          <div
                            id={`recommend-${idx}`}
                            className="h-full bg-neutral-800"
                          >
                            <ConnectionUserProfile
                              item={item}
                              cardClassName="!bg-neutral-800"
                            />
                          </div>
                        )}
                      />
                      <div className="bg-neutral-800 px-4 py-[10px] mt-6 mb-4">
                        <p className="text-sm text-[#D9D9D9]">
                          Global Search (
                          {dataSearchConnections?.global?.length || 0})
                        </p>
                      </div>
                      <SwiperMC
                        props={{
                          breakpoints: {
                            1023: {
                              slidesPerView: 1,
                              grid: {
                                rows: 1,
                                fill: "row",
                              },
                            },
                            1439: {
                              slidesPerView: 1,
                              grid: {
                                rows: 1,
                                fill: "row",
                              },
                            },
                          },
                          slidesPerGroup: 1,
                          wrapperClass: "pt-[48px!important]",
                          spaceBetween: 16,
                          grabCursor: true,
                          modules: [Grid],
                        }}
                        classNameSlide="mb-[80px!important] h-[auto!important]"
                        data={dataSearchConnections?.global || []}
                        render={(item, idx) => (
                          <div
                            id={`recommend-${idx}`}
                            className="h-full bg-neutral-800"
                          >
                            <ConnectionUserProfile
                              item={item}
                              cardClassName="!bg-neutral-800"
                            />
                          </div>
                        )}
                      />
                    </>
                  )}
                </section>
              </div>
            </div>
          )}
        </div>
      </LayoutDashboard>

      {isModalRequestChampion && (
        <ModalRequestCircleChampion
          totalChampionLeft={countAddUserChampionLeft as number}
          isOpen={isModalRequestChampion}
          onClose={() => setIsModalRequestChampion(false)}
          onSuccess={(resp: any) => {
            refetchSearchPendingUserChampion();
            api.success({
              message: resp?.title,
              description: resp?.description,
            });

            setIsModalRequestChampion(false);
            refetch();
          }}
          onError={(error: any) => {
            api.error({
              message: error?.title,
              description: error?.description,
            });
          }}
        />
      )}

      {showModalRecommendation && (
        <Modal
          width={572}
          open={showModalRecommendation}
          title={`Directory`}
          footer={null}
          onCancel={() => {
            setShowModalRecommendation(false);
            setSearchValue("");
          }}
        >
          <div
            className="py-4 max-h-[75vh] overflow-y-auto overflow-x-hidden"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {
              <>
                <div className="mb-8 mt-4">
                  <Input.Search
                    className="no-border-radius-input-antd"
                    placeholder="Search users..."
                    allowClear
                    enterButton
                    onReset={() => {
                      setSearchValue("");
                    }}
                    onSearch={(value) => {
                      setSearchValue(value);
                    }}
                  />
                </div>

                {isLoadingRecommendConnection ? (
                  <div className="grid gap-4 grid-cols-1">
                    <div className="animate-pulse">
                      <div className="w-full h-24 bg-neutral-700 rounded-lg" />
                    </div>
                    <div className="animate-pulse">
                      <div className="w-full h-24 bg-neutral-700 rounded-lg" />
                    </div>
                  </div>
                ) : (
                  <div className="-mx-6 px-6 overflow-auto">
                    {resultDataRecommendedConnection &&
                    resultDataRecommendedConnection?.length > 0 ? (
                      <div className="grid grid-col-1 gap-4">
                        {orderBy(
                          resultDataRecommendedConnection,
                          [(data) => data?.user.lastName.toLowerCase()],
                          ["asc"]
                        )
                          ?.filter(({ user }) =>
                            searchValue
                              ? user.firstName
                                  .toLowerCase()
                                  .includes(searchValue.toLowerCase())
                              : true
                          )
                          .map((item: any, idx: number) => (
                            <div
                              className="flex items-center justify-between w-full mb-4"
                              key={`item-of-mutual-${idx}`}
                            >
                              <CardUserProfile
                                nameClassName="pr-4"
                                isNameCapitalized={false}
                                disableHref
                                item={{
                                  ...item.user,
                                  firstName: truncateText(item.user.firstName, {
                                    sm: 50,
                                    lg: 50,
                                  }),
                                  avatarUrl: item?.user.avatarUrl!,
                                }}
                                CGP={item.CGPercent}
                                variant="horizontal"
                                avatarSize="sm"
                              />
                              <div
                                className="text-yellow cursor-pointer"
                                onClick={() => quickConnect(item.user.id)}
                              >
                                {connectingLoadingIDs.includes(item.user.id) ? (
                                  <LoadingOutlined />
                                ) : (
                                  <IoAdd size={24} />
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                  </div>
                )}
              </>
            }
          </div>
        </Modal>
      )}

      <Modal
        title={`Cancel Invitation`}
        footer={[
          <Button
            key="back"
            onClick={() => modalCancelCocInvitationConfirmSet(null)}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isLoadingCancelCocInvitation}
            onClick={() =>
              submitCancelInvitation(
                modalCancelCocInvitationConfirm as typeCancelInvitation
              )
            }
          >
            Yes, Cancel Invitation
          </Button>,
        ]}
        open={modalCancelCocInvitationConfirm ? true : false}
        onCancel={() => modalCancelCocInvitationConfirmSet(null)}
      >
        <p>
          Are you sure you want to cancel this Circle of Champions invitation?
        </p>
      </Modal>

      <ModalPendingCoc
        isOpen={modalConfig.key === "list-pending"}
        onCancel={() => modalConfigSet({ key: null })}
        data={dataSearchPendingUserChampion}
        onCancelInvitation={(payload) => onChoosedCancelCoc(payload)}
        isLoadingGetCancelUser={isLoadingGetCancelUser}
      />
    </>
  );
};

const removeDuplicatesUserById = (users: any[]): any[] => {
  const uniqueUsers = users?.reduce((acc: any, currentUser: any) => {
    if (!acc?.some((user: any) => user?.user?.id === currentUser?.user?.id)) {
      acc?.push(currentUser);
    }
    return acc;
  }, []);

  return uniqueUsers;
};

export default Connections;
